import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { InventoryReturnControl } from './InventoryReturnControl'
import { useProcessMaterialLots } from '@/components/process/materials'
import { useReturnableInventory } from '@/hooks/useProcessSteps'
import {
  allocatedQty,
  batchHint,
  byExpiryThenLot,
  expiryDate,
  formatQty,
  isFullyAllocated,
  offeredLots,
  outstandingQty,
  selectableTotal,
  takeFromLot,
  tidyQty,
  type LotAllocations,
  type TransitionMode,
} from '@/lib/process/allocation'
import type { ProcessStepConnection } from '@/api/processStepsApi'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * Whether a lot is past its expiry, in the operator's terms.
 *
 * Expiry is a DATE: a lot expiring today is good for the whole of today, which
 * is why this compares date strings and not instants. Mirrors `isExpiredOn` in
 * cgt-backend/src/inventory/kits/lot-eligibility.ts — where the server holds
 * the authority (kit runs, which refuse an expired lot with a 409) this only
 * spares the operator a round trip. Everywhere else it is what keeps expired
 * stock off the list in the first place.
 */
export function isExpiredLot(expiry: string | null | undefined): boolean {
  if (!expiry) return false
  return expiry.slice(0, 10) < new Date().toISOString().slice(0, 10)
}

/** Beyond this many lots the list gets a search box of its own. */
const SEARCHABLE_FROM = 6
/** Beyond this many the list scrolls rather than growing the dialog. */
const SCROLL_FROM = 4

/**
 * Which lots go into a requirement — and only that.
 *
 * **The operator never types a quantity here.** How much of a material the step
 * consumes is configured on the process (`qtyRequired`) and is therefore already
 * known; asking for it again invited a number that disagreed with the
 * configuration, and since ending requires the allocation to sum to exactly the
 * configured quantity, any disagreement showed up much later as a Stop button
 * that would not light with nothing on screen pointing at the box responsible.
 *
 * Picking a lot takes as much of what is still outstanding as that lot can
 * cover, so the total is right by construction and a requirement needing more
 * than one lot is served by picking a second one. Allocation follows PICK ORDER
 * rather than expiry: which lot leaves the shelf is the operator's call, made
 * against the physical stock in front of them, and the same reasoning is why
 * Start no longer prefills anything. The FEFO ordering below is what the
 * operator SEES, not what the code allocates.
 *
 * Unpicking returns only that lot's share to the pool and leaves the other
 * picks alone — re-spreading the remainder under the operator would silently
 * rewrite allocations they had already decided on.
 *
 * What a tick MEANS is stated throughout: it places a real hold. Start reserves
 * it (`reserveInventoryFor`, backend process-step executions service) and End
 * settles it against the lot. Nothing about a checkbox beside a lot number
 * conveys that the operator is committing stock other runs can then no longer
 * have.
 */
export function MaterialLotAllocator({
  submissionId,
  fieldId,
  requirement,
  allocations,
  onAllocate,
  onAllocateMany,
  reservedByLot,
  blockExpired,
  lockQuantity,
  mode,
  open,
  onToggle,
  quantityMultiplier,
}: {
  /** Identifies the execution the `[+]` return acts on. */
  submissionId: string
  fieldId: string
  requirement: ProcessStepConnection['inventoryRequirements'][number]
  allocations: LotAllocations
  /**
   * `commit` marks a DELIBERATE, finished value — a tick, an untick, or a
   * quantity the operator has finished typing — and is the only thing that may
   * release a hold.
   *
   * Every keystroke reaches this callback, and releasing on each one returned
   * real stock for values the operator was only passing through: editing 10 to
   * 15 goes via "1" and handed 9 units back, and clearing the box first gave up
   * the whole hold. Each of those also wrote its own row to the audit trail.
   */
  onAllocate: (lot: string, qty: string, commit?: boolean) => void
  /**
   * Several lots at once, already settled on the server.
   *
   * The `[+]` return is the only caller. One call rather than one per lot
   * because the parent merges each write from the render it was given: two lots
   * returned together made the second write drop the first, leaving that lot
   * claiming an allocation larger than its hold. Releases nothing — the stock
   * has already moved.
   */
  onAllocateMany?: (updates: Record<string, string>) => void
  /**
   * How much of THIS requirement's picks is actually reserved server-side —
   * placed via the "Reserve" button (z8nrz7dyfp follow-up), keyed by lot.
   * Distinct from `allocations`, which is just what the operator has ticked:
   * a fully-ticked row is not yet a hold, and the badge must not claim
   * "Reserved" for a pick nothing has confirmed to the server.
   */
  reservedByLot?: Record<string, number>
  /**
   * Whether an expired lot that IS listed may still be ticked.
   *
   * No step offers expired stock any more — `offeredLots` drops it — so the
   * only expired row on screen is one this execution already drew from, and
   * this decides whether unticking it is reversible. Kit runs refuse an expired
   * lot outright (409), so re-ticking one would only earn an error; anywhere
   * else the record must be able to go back to saying an expired lot was used,
   * because that is what happened.
   */
  blockExpired?: boolean
  /**
   * Kit runs take the derived split and nothing else.
   *
   * A kit is assembled to a fixed recipe and its components are verified
   * against it, so an operator-chosen split is not a decision the kit path can
   * honour. Everywhere else the derived figure is a STARTING POINT: real stock
   * comes off part-used containers, and the operator has to be able to say "3
   * from this lot, 2 from that one" when that is what physically happened.
   */
  lockQuantity?: boolean
  /** Drives the consequence sentence — reserving (start) vs consuming (end). */
  mode: TransitionMode
  /**
   * Controlled by the Inventory tab, not held here: only the first material
   * opens by default, and "Expand all" has to move every row at once — neither
   * is decidable from inside a single row.
   */
  open: boolean
  onToggle: () => void
  /**
   * The run's batch size, for the "10 × 100 units" provenance line.
   *
   * `requirement.qtyRequired` arrives ALREADY scaled by it (the server does the
   * multiplication so this and the End validation cannot drift), so this is
   * only ever used to explain the number — never to compute it.
   */
  quantityMultiplier?: number
}) {
  // Only this requirement's material (z8nrz7e8kw).
  const { lotsFor } = useProcessMaterialLots([requirement.consumableId])
  const locate = useLocationText()
  const label = requirement.itemLabel ?? requirement.consumableId
  const uomSuffix = requirement.uom ? ` ${requirement.uom}` : ''

  /**
   * What THIS step already holds on each lot.
   *
   * A lot's `reservedQuantity` counts every open hold in the tenant, this
   * step's own included, and `available` is the shelf minus all of them. Read
   * raw, both figures describe the lot from nobody's point of view: a step
   * holding 1 of a 20-lot was told "19 of 20 available · 1 held by other
   * runs", when the 1 was its own, and a step holding a lot outright saw
   * "0 available" on stock it was standing on — un-tickable, and warned
   * against in amber for a quantity the server would have accepted.
   *
   * Same query the `[+]` return control reads, so TanStack serves both from
   * one fetch, and it is the authority on this step's holds rather than
   * `reservedByLot`, which only remembers reserves made in this session.
   */
  const ownHolds = useReturnableInventory(submissionId, fieldId, requirement.id, true)
  const ownHeldByLot = useMemo(() => {
    const byLot = new Map<string, number>()
    for (const h of ownHolds.data ?? []) {
      byLot.set(h.lotNumber, (byLot.get(h.lotNumber) ?? 0) + h.qty)
    }
    return byLot
  }, [ownHolds.data])

  // Sorted for READING — first to expire first. Picking still follows click
  // order; see the note on the component.
  //
  // `available` is rewritten to what THIS step may draw — the shelf figure plus
  // its own hold — once, here, rather than at each place that reads it. Every
  // derivation downstream (`offeredLots`, `selectableTotal`, `takeFromLot`, the
  // exhausted test, the over-allocation warning) then inherits the step's point
  // of view for free, which is the only way they cannot disagree: a step
  // holding a lot outright otherwise saw `0 available` and could neither re-tick
  // it nor be proposed a quantity from it, while the server would have taken
  // the pick happily.
  const onHand = useMemo(() => {
    const sorted = [...lotsFor(requirement.consumableId)].sort(byExpiryThenLot)
    return sorted.map(l => {
      const ownHeld = ownHeldByLot.get(l.lot) ?? 0
      return ownHeld ? { ...l, available: l.available + ownHeld } : l
    })
  }, [lotsFor, requirement.consumableId, ownHeldByLot])

  /**
   * Lots this row has seen a quantity against, kept once seen.
   *
   * It ACCUMULATES rather than reading the current allocation, for two reasons:
   * End mode seeds its allocations from the execution after this row has
   * already mounted, so a first-render snapshot would miss them; and unticking
   * an expired lot would otherwise delete the only row that can put it back.
   */
  const [recorded, setRecorded] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    const picked = Object.keys(allocations).filter(l => Number(allocations[l] ?? 0) > 0)
    setRecorded(prev =>
      picked.every(l => prev.has(l)) ? prev : new Set([...prev, ...picked]),
    )
  }, [allocations])

  // Expired stock is off the list entirely; see `offeredLots`.
  const lots = useMemo(
    () => offeredLots(onHand, isExpiredLot, l => recorded.has(l)),
    [onHand, recorded],
  )

  const allocated = allocatedQty(allocations)
  const matched = isFullyAllocated(requirement.qtyRequired, allocations)
  /**
   * What is actually HELD, versus what is merely ticked. `matched` alone used
   * to be treated as "Reserved" in the badge below, which claimed a hold that
   * might not exist yet — the operator could tick every lot, never press
   * Reserve, and still see a green "Reserved" pill.
   *
   * Read from the server's own open holds first (`ownHeldByLot`, the returnable
   * query) and only then from `reservedByLot`, which remembers reserves made in
   * THIS modal session and is empty after a remount. Taking the larger of the
   * two keeps the badge steady in the moment between a successful Reserve and
   * the refetch that confirms it.
   */
  const heldOn = useCallback(
    (lot: string) => Math.max(Number(reservedByLot?.[lot] ?? 0) || 0, ownHeldByLot.get(lot) ?? 0),
    [reservedByLot, ownHeldByLot],
  )
  /**
   * Every unit currently picked is held — checked PER LOT.
   *
   * Comparing the two totals let a hold on one lot vouch for an allocation on
   * another: 5 held on lot A, unticked but not yet released (in flight, or the
   * release skipped because `lotsFor` had no id), then 5 ticked on lot B which
   * nothing holds. The sums matched, so the row went green and claimed "this
   * stock is held for the run" while lot B had no hold at all.
   *
   * Deliberately NOT gated on `matched`. "Is all of this reserved?" and "does it
   * cover the configured quantity?" are two different questions, and since a
   * step may record drawing LESS than it configures, the first one stands on its
   * own: an operator who reserved 6 of a configured 10 has genuinely reserved
   * them, and being told "6 of 10 allocated" denied the hold they just placed.
   */
  const fullyReserved =
    allocated > 1e-6 &&
    Object.entries(allocations).every(([lot, raw]) => {
      const want = Number(raw) || 0
      return want <= 1e-6 || heldOn(lot) + 1e-6 >= want
    })
  const outstanding = outstandingQty(requirement.qtyRequired, allocations)
  const pool = selectableTotal(lots, isExpiredLot)
  const short = !matched && pool < requirement.qtyRequired
  const pickedCount = lots.filter(l => Number(allocations[l.lot] ?? 0) > 0).length

  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? lots.filter(l => l.lot.toLowerCase().includes(q)) : lots
  }, [lots, query])

  /**
   * ONE line about this material, never a stack of them.
   *
   * These conditions overlap — a material with no lots on hand is also short of
   * its requirement and also has nothing to pick — so rendering them
   * independently put three notices under a single row and turned a routine
   * "there is none of this in stock" into something that reads like a fault.
   * The most specific true statement wins and the rest stay quiet.
   */
  const note: { text: string; tone: 'plain' | 'warn' } =
    lots.length === 0
      ? {
          // "No lots" and "no lots you may use" send the operator to different
          // places — the storeroom, or whoever can release replacement stock —
          // so the expired case says so rather than reading as empty shelves.
          text:
            onHand.length > 0
              ? 'Every lot of this material has expired, so none can be used.'
              : 'No lots on hand for this material.',
          tone: 'warn',
        }
      : short
        ? {
            text: `Only ${tidyQty(pool)}${uomSuffix} on hand, and this step needs ${requirement.qtyRequired}${uomSuffix}.`,
            tone: 'warn',
          }
        : matched
          ? {
              text:
                mode === 'start'
                  ? fullyReserved
                    ? 'Reserved. This stock is held for the run.'
                    : 'Fully allocated, but not yet reserved — click Reserve above, or it is held automatically when you sign Start.'
                  : mode === 'end'
                    ? 'Fully allocated. This stock is consumed when you sign Stop.'
                    : 'Fully allocated.',
              tone: 'plain',
            }
          : // Held, but less than the step configures. A legitimate record — the
            // step says what it expects to draw, this says what is actually being
            // drawn — so it states the position rather than nagging for the rest.
            fullyReserved && mode === 'start'
            ? {
                text: `Reserved. ${tidyQty(allocated)}${uomSuffix} is held for the run, ${tidyQty(outstanding)}${uomSuffix} less than this step configures. Tick more if that is not right.`,
                tone: 'plain',
              }
            : {
              text: lockQuantity
                ? `Tick the lots this came off — each takes what is still outstanding (${tidyQty(outstanding)}${uomSuffix}). The quantity is set by the kit recipe.`
                : `Tick the lots this came off — each takes what is still outstanding (${tidyQty(outstanding)}${uomSuffix}), and you can edit the amount to split it as the shelf actually dictates.`,
              tone: 'plain',
            }

  /**
   * Lots the operator has ticked, held apart from their quantities.
   *
   * A lot is normally "picked" because it carries a quantity, but an editable
   * box passes through empty on the way to a new number — and deriving picked
   * from the quantity alone unmounted the input under the cursor the moment it
   * was cleared. The tick is the decision; the quantity is what it is worth.
   */
  const [heldPicks, setHeldPicks] = useState<Set<string>>(new Set())
  const isPicked = (lot: string) => Number(allocations[lot] ?? 0) > 0 || heldPicks.has(lot)

  /**
   * The header badge's word and color. Only mode 'start' distinguishes ticked
   * from reserved — 'end'/'complete' have no Reserve button, and "fully
   * accounted for" is the only claim `matched` ever needs to make there.
   */
  // The WORD says whether the stock is held; the COLOUR says whether it covers
  // what the step configures. Green is "held, and the full configured amount";
  // blue is "held, deliberately less" — a legitimate record now, not a warning;
  // amber is "picked, but nothing is holding it yet".
  const badge =
    mode === 'start'
      ? fullyReserved
        ? {
            word: 'reserved',
            tone: matched ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800',
          }
        : { word: 'allocated', tone: matched ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800' }
      : { word: 'reserved', tone: matched ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800' }

  const toggleLot = (lot: string, take: number, picked: boolean) => {
    setHeldPicks(prev => {
      const next = new Set(prev)
      if (picked) next.delete(lot)
      else next.add(lot)
      return next
    })
    // Committed: a tick and an untick are both finished decisions, so an untick
    // may release its hold straight away.
    if (picked) onAllocate(lot, '0', true)
    else if (take > 0) onAllocate(lot, String(take), true)
  }

  return (
    <li
      className={cn(
        'overflow-hidden rounded-lg border bg-white',
        (mode === 'start' ? fullyReserved : matched) ? 'border-green-300' : 'border-gray-200',
      )}
    >
      {/* A disclosure, deliberately not a checkbox: the checkboxes below mean
          "this lot goes in", and a control of the same shape at the header
          level read as though it selected the material itself. */}
      <div className='flex items-center bg-gray-50/70 pr-2'>
        <button
          type='button'
          onClick={onToggle}
          aria-expanded={open}
          className='flex min-w-0 flex-1 items-center gap-2.5 py-2.5 pl-3 pr-1 text-left transition-colors hover:bg-gray-100/70'
        >
          {open ? (
            <ChevronDown className='h-4 w-4 shrink-0 text-gray-400' />
          ) : (
            <ChevronRight className='h-4 w-4 shrink-0 text-gray-400' />
          )}
          <span className='min-w-0 flex-1 truncate text-sm font-medium text-gray-800'>{label}</span>
          <span className='shrink-0 text-xs text-gray-500'>
            Reserve {formatQty(requirement.qtyRequired, requirement.uom)}
            {batchHint(requirement, quantityMultiplier) && (
              <span className='ml-1.5 text-[11px] text-gray-400'>
                ({batchHint(requirement, quantityMultiplier)})
              </span>
            )}
          </span>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums',
              badge.tone,
            )}
          >
            {tidyQty(allocated)} of {requirement.qtyRequired} {badge.word}
          </span>
        </button>

        {/* A SIBLING of the disclosure, never a child: a button inside a button
            is invalid markup, and returning stock is its own action — it must
            not open or close the lot list. Renders nothing unless this
            requirement actually has an open hold. */}
        <InventoryReturnControl
          submissionId={submissionId}
          fieldId={fieldId}
          requirementId={requirement.id}
          // Take the returned amount straight off what this row already holds,
          // rather than waiting on a reseed from the execution that the parent
          // deliberately skips once a modal-open session has already seeded
          // (so an in-progress pick elsewhere on the form isn't clobbered).
          // This is the only path that keeps the "reserved" badge and the
          // returnable list in agreement without a full page reload.
          // Keyed by lot NUMBER, matching every other write on this row
          // (`onAllocate(l.lot, …)`). Keying by the database id instead wrote
          // a stray entry that no lot read back, so the chip above kept
          // reporting stock the step had already given up.
          // One batched call, and NOT through `onAllocate`: the return has
          // already moved this stock server-side, so re-releasing the
          // difference would take the remainder with it — returning 5 of 10
          // released the 10, re-held 5, then released that 5 too. Batched
          // because the parent merges each write from the render it was given,
          // so per-lot calls made the second drop the first.
          onReturned={(lines) => {
            const updates: Record<string, string> = {}
            for (const { lotNumber, qty } of lines) {
              const current = Number(allocations[lotNumber] ?? 0)
              updates[lotNumber] = String(Math.max(0, current - qty))
            }
            onAllocateMany?.(updates)
          }}
        />
      </div>

      {open && (
        <div className='px-3 py-2.5'>
          <p
            className={cn(
              'mb-2 text-[11px]',
              note.tone === 'warn' ? 'font-medium text-amber-700' : 'text-gray-500',
            )}
          >
            {note.text}
          </p>

          {lots.length > 0 && (
            <>
              <div className='mb-2 flex items-center gap-2'>
                {lots.length >= SEARCHABLE_FROM && (
                  <div className='relative min-w-0 flex-1'>
                    <Search className='pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400' />
                    <input
                      type='search'
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder={`Search ${lots.length} lots…`}
                      aria-label={`Search lots for ${label}`}
                      className='h-8 w-full rounded-md border border-input bg-white pl-7 pr-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    />
                  </div>
                )}
                {/* What is picked is the point of the row, so it reads as a status
                    pill, not a footnote (z8nrz7fn5r). */}
                {pickedCount > 0 ? (
                  <p className='ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-600 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white'>
                    <Check className='h-3.5 w-3.5' />
                    {pickedCount} of {lots.length} lots picked
                  </p>
                ) : (
                  <p className='ml-auto shrink-0 text-[11px] tabular-nums text-gray-500'>
                    {`${lots.length} lot${lots.length === 1 ? '' : 's'} · first to expire listed first`}
                  </p>
                )}
              </div>

              {/* A material can carry dozens of lots. The list scrolls in place
                  rather than growing the dialog, so the running total and the
                  step's own actions never leave the screen. */}
              <div
                className={cn(
                  'divide-y divide-gray-100 rounded-md border border-gray-200',
                  lots.length > SCROLL_FROM && 'max-h-56 overflow-y-auto',
                )}
              >
                {filtered.length === 0 ? (
                  <p className='px-3 py-4 text-center text-xs text-gray-400'>
                    No lot matches “{query}”.
                  </p>
                ) : (
                  filtered.map(l => {
                    const expired = !!blockExpired && isExpiredLot(l.expiry)
                    const mine = Number(allocations[l.lot] ?? 0)
                    const picked = isPicked(l.lot)
                    const take = takeFromLot(l, outstanding)
                    // `l.available` is already step-relative (see `onHand`), so
                    // it IS what this step may draw. The split below is only
                    // for the subtext: of everything held on the lot, how much
                    // is this step's own and how much is genuinely elsewhere.
                    const ownHeld = ownHeldByLot.get(l.lot) ?? 0
                    const drawable = l.available
                    const heldByOthers = Math.max(0, (l.reservedQuantity ?? 0) - ownHeld)
                    // Nothing this lot can contribute right now: it is spent,
                    // expired, or the requirement is already covered. Picked
                    // lots stay enabled so the choice can always be taken back.
                    // Quarantined, and only still listed because this execution
                    // already drew from it — `offeredLots` drops quarantined
                    // stock otherwise. The record must keep showing the lot it
                    // used, but the server refuses to reserve one (400), so the
                    // row has to say so rather than leaving a tick that earns an
                    // error on Reserve. Treated exactly like an expired lot:
                    // never newly tickable, always UN-tickable, so the operator
                    // can take the choice back.
                    const quarantined = l.gateStatus === 'quarantine'
                    const exhausted =
                      !picked && (expired || quarantined || drawable <= 0 || take <= 0)
                    return (
                      <label
                        key={l.lot}
                        className={cn(
                          'flex items-center gap-2.5 px-2.5 py-2 transition-colors',
                          picked && 'bg-blue-50/50',
                          exhausted ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50',
                        )}
                        title={
                          quarantined
                            ? 'This lot is in quarantine and cannot be reserved. Untick it to continue.'
                            : expired
                              ? 'This lot has expired and cannot go into a kit.'
                              : drawable <= 0
                                ? 'Nothing left on this lot.'
                                : exhausted
                                  ? 'The required quantity is already allocated.'
                                  : `Draw ${label} from lot ${l.lot}`
                        }
                      >
                        <Checkbox
                          checked={picked}
                          disabled={exhausted}
                          onCheckedChange={() => toggleLot(l.lot, take, picked)}
                          aria-label={`Use lot ${l.lot} for ${label}`}
                        />
                        <span className='min-w-0 flex-1'>
                          <span
                            className={cn(
                              'block truncate text-xs',
                              expired || quarantined
                                ? 'text-gray-400 line-through'
                                : 'text-gray-700',
                            )}
                          >
                            <span className='font-medium'>{l.lot}</span>
                            {l.expiry ? (
                              <span
                                className={cn(
                                  'ml-1.5',
                                  isExpiredLot(l.expiry) ? 'text-red-600' : 'text-gray-400',
                                )}
                              >
                                exp {expiryDate(l.expiry)}
                              </span>
                            ) : null}
                            {l.storagePath ? (
                              <span className='ml-1.5 text-gray-400'>· {locate(l.storagePath)}</span>
                            ) : l.locationCode ? (
                              <span className='ml-1.5 text-gray-400'>· {l.locationCode}</span>
                            ) : null}
                          </span>
                          {/* An expired or quarantined lot is only ever listed
                              because this step already drew from it — say so, or
                              the row reads as an offer the rules elsewhere
                              refuse. Quarantine is stated first: it is the newer
                              fact and the one the server will reject on. */}
                          {quarantined ? (
                            <span className='block text-[11px] font-medium text-red-600'>
                              Quarantined — {picked
                                ? 'untick it to reserve the rest'
                                : 'it cannot be used'}
                              .
                            </span>
                          ) : isExpiredLot(l.expiry) ? (
                            <span className='block text-[11px] font-medium text-red-600'>
                              Expired — listed because this step already drew from it.
                            </span>
                          ) : null}
                          {/* Why a lot with stock on the shelf may still be
                              unavailable. `available` alone cannot tell a
                              nearly-spent lot from a merely busy one — and the
                              two holds answer different questions, so they are
                              never merged into one figure: what this step has
                              already taken is undone here, what another run
                              holds is not. */}
                          {ownHeld ? (
                            <span className='block text-[11px] text-blue-600'>
                              {tidyQty(ownHeld)} held by this step
                            </span>
                          ) : null}
                          {heldByOthers ? (
                            <span className='block text-[11px] text-gray-400'>
                              {tidyQty(heldByOthers)} held by other runs
                            </span>
                          ) : null}
                        </span>

                        <span
                          className={cn(
                            'shrink-0 text-xs tabular-nums',
                            drawable <= 0 ? 'text-red-600' : 'text-gray-500',
                          )}
                        >
                          {typeof l.quantity === 'number' && l.quantity !== drawable
                            ? `${tidyQty(drawable)} of ${tidyQty(l.quantity)} available`
                            : `${tidyQty(drawable)} available`}
                        </span>

                        {/* The share this lot contributes.
                            Derived on tick either way; on a kit run that is
                            final, elsewhere it stays editable so a split the
                            shelf actually dictates can be recorded. */}
                        {picked && !lockQuantity ? (
                          <input
                            type='number'
                            inputMode='decimal'
                            min={0}
                            step='any'
                            value={allocations[l.lot] ?? ''}
                            aria-label={`Quantity from ${l.lot} for ${label}`}
                            title='Adjust how much comes off this lot'
                            // The checkbox owns pick/unpick; a click that lands
                            // in the box must not toggle the row underneath it.
                            onClick={e => e.stopPropagation()}
                            // Typing records the figure but releases nothing:
                            // every keystroke lands here, and 10 edited to 15
                            // passes through "1", which used to hand 9 units
                            // back to stock (and write a return to the audit
                            // trail) before the operator had finished the
                            // number. The release waits for a finished value.
                            onChange={e => onAllocate(l.lot, e.target.value)}
                            onBlur={e => onAllocate(l.lot, e.target.value, true)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                onAllocate(l.lot, e.currentTarget.value, true)
                              }
                            }}
                            className={cn(
                              'h-7 w-20 shrink-0 rounded-md border bg-white px-2 text-right text-xs font-medium tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              mine > drawable
                                ? 'border-amber-400 bg-amber-50 text-amber-800'
                                : 'border-input text-green-700',
                            )}
                          />
                        ) : (
                          <span
                            className={cn(
                              'w-20 shrink-0 text-right text-xs font-medium tabular-nums',
                              picked ? 'text-green-700' : 'text-transparent',
                            )}
                          >
                            {picked ? `${tidyQty(mine)}${uomSuffix}` : '0'}
                          </span>
                        )}
                      </label>
                    )
                  })
                )}
              </div>
            </>
          )}
        </div>
      )}
    </li>
  )
}
